import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_4aa3e30f-645a-43e8-83ed-b83576a14c9f/artifacts/p43xkrnl_SusTek%20Colored%20Logo%20Name%20-%20Horizontal.svg";

const SupplierAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(inviteToken ? "register" : "login");
  
  // Login form state
  const [loginData, setLoginData] = useState({
    email: "",
    password: ""
  });
  
  // Register form state
  const [registerData, setRegisterData] = useState({
    company_name: "",
    email: "",
    password: "",
    confirm_password: "",
    invite_token: inviteToken || ""
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/suppliers/login`, {
        email: loginData.email,
        password: loginData.password
      });
      
      // Store supplier info in localStorage
      localStorage.setItem("supplier", JSON.stringify(response.data));
      toast.success("Login successful!");
      navigate("/questionnaire");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (registerData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/suppliers/register`, {
        company_name: registerData.company_name,
        email: registerData.email,
        password: registerData.password,
        invite_token: registerData.invite_token || null
      });
      
      // Store supplier info in localStorage
      localStorage.setItem("supplier", JSON.stringify(response.data));
      toast.success("Registration successful!");
      navigate("/questionnaire");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-white flex flex-col">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50" data-testid="auth-header">
        <div className="container-main py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="auth-logo">
            <img src={LOGO_HORIZONTAL} alt="SusTek" className="h-10" />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-forest hover:bg-forest/10" data-testid="auth-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md animate-fade-in">
          <Card className="border-border/50 shadow-lg" data-testid="auth-card">
            <CardHeader className="text-center">
              <CardTitle className="font-heading text-2xl text-foreground">Supplier Portal</CardTitle>
              <CardDescription>
                {inviteToken 
                  ? "You've been invited to complete the ESG questionnaire"
                  : "Login or register to complete your ESG questionnaire"
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="auth-login-tab">Login</TabsTrigger>
                  <TabsTrigger value="register" data-testid="auth-register-tab">Register</TabsTrigger>
                </TabsList>
                
                {/* Login Tab */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="supplier@company.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="Enter your password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        data-testid="login-password-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full btn-primary"
                      disabled={isLoading}
                      data-testid="login-submit-btn"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Logging in...
                        </>
                      ) : (
                        "Login"
                      )}
                    </Button>
                  </form>
                </TabsContent>
                
                {/* Register Tab */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-company">Company Name</Label>
                      <Input
                        id="register-company"
                        type="text"
                        placeholder="Your Company Ltd."
                        value={registerData.company_name}
                        onChange={(e) => setRegisterData({ ...registerData, company_name: e.target.value })}
                        required
                        data-testid="register-company-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="supplier@company.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="Create a password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        data-testid="register-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-confirm">Confirm Password</Label>
                      <Input
                        id="register-confirm"
                        type="password"
                        placeholder="Confirm your password"
                        value={registerData.confirm_password}
                        onChange={(e) => setRegisterData({ ...registerData, confirm_password: e.target.value })}
                        required
                        data-testid="register-confirm-input"
                      />
                    </div>
                    {inviteToken && (
                      <div className="space-y-2">
                        <Label htmlFor="register-invite">Invite Token</Label>
                        <Input
                          id="register-invite"
                          type="text"
                          value={registerData.invite_token}
                          onChange={(e) => setRegisterData({ ...registerData, invite_token: e.target.value })}
                          className="bg-muted"
                          data-testid="register-invite-input"
                        />
                        <p className="text-xs text-muted-foreground">Invite token detected from your link</p>
                      </div>
                    )}
                    <Button 
                      type="submit" 
                      className="w-full btn-primary"
                      disabled={isLoading}
                      data-testid="register-submit-btn"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Registering...
                        </>
                      ) : (
                        "Register"
                      )}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-border bg-cream">
        <div className="container-main text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} SusTek Sustainable Supply Chain Portal. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default SupplierAuth;
